<template>
  <div class="modal-trigger-btn" @click="handleDefaultOpen">
    <slot name="slider-modal-trigger-btn"></slot>
  </div>

  <Teleport :to="teleportTo" :disabled="!teleportTo">
    <Transition :name="modalHeaderAnimation">
      <div v-show="canShowModal" class="modal-header-grid">
        <div class="modal-btn-left">
          <button class="modal-btn" @click="handleDefaultClose">
            <UiIcon
              size="var(--size-typography-size-16)"
              :name="modalCloseBtnIcon"
              :is-clickable="true"
            />
          </button>
        </div>

        <h3 class="modal-title headline-l">{{ modalTitle }}</h3>

        <div class="modal-btn-right body-m">
          <button
            v-if="modalClearBtnText"
            class="modal-btn"
            @click="emit('clear')"
          >
            {{ modalClearBtnText }}
          </button>
        </div>
      </div>
    </Transition>

    <Transition name="slide">
      <div v-show="canShowModal" class="modal-content">
        <slot name="slider-modal-content"></slot>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import UiIcon from '@sephora-asia/ui-icon'

interface Props {
  modalTitle?: string
  modalHeaderAnimation?: 'slide' | 'teleport'
  modalClearBtnText?: string
  modalCloseBtnIcon?: 'close' | 'chevron'
  teleportTo?: string
}
withDefaults(defineProps<Props>(), {
  modalTitle: '',
  modalHeaderAnimation: 'teleport',
  modalClearBtnText: '',
  modalCloseBtnIcon: 'close',
  teleportTo: undefined,
})
interface Emits {
  (e: 'clear' | 'defaultClosed' | 'defaultOpened'): void
}
const emit = defineEmits<Emits>()

const store = useProductListingFiltersStore()
const canShowModal = ref(false)

watch(
  () => store.toCloseAllMwebModals,
  () => {
    if (store.toCloseAllMwebModals) {
      canShowModal.value = false
    }
  }
)

function handleDefaultOpen() {
  canShowModal.value = true
  emit('defaultOpened')
}

// Non-default close example: toCloseAllMwebModals
function handleDefaultClose() {
  canShowModal.value = false
  emit('defaultClosed')
}
</script>

<style lang="scss" scoped>
@mixin base-modal-styles {
  --base-gap-size: 16px;
  --modal-header-offset: 48px;
  position: fixed;
  left: 0;
  right: 0;

  background-color: var(--color-background-base);
  z-index: var(--size-z-index-modal);
}

.modal-trigger-btn {
  width: 100%;
  height: 100%;
}

.modal-header-grid {
  @include base-modal-styles;
  top: 0;
  height: var(--modal-header-offset);
  background-color: var(--color-base-grey-05);

  display: grid;
  grid: 1fr / 1fr 1fr 1fr;
  align-items: center;
  text-align: center;

  .modal-btn {
    // Enhance clickable area
    height: var(--modal-header-offset);
    padding: 0 var(--base-gap-size);

    &-left {
      display: flex;
    }
    &-right {
      display: flex;
      flex-direction: row-reverse;
    }
  }
}

.modal-content {
  @include base-modal-styles;
  top: var(--modal-header-offset);
  bottom: 0;
  background-color: var(--color-background-base);

  overflow: scroll;
  padding-bottom: calc(
    var(--base-gap-size) * 6
  ); // To prevent the modal content from covering top/bottom navigation
}

button {
  all: unset;
  line-height: 0;
  cursor: pointer;
}

// Slide modal animation
.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
.slide-leave-active,
.slide-enter-active {
  transition: 0.3s ease;
}

// Teleport modal animation
.teleport-enter-to,
.teleport-leave-from {
  transform: translateX(0);
}
.teleport-enter-from,
.teleport-leave-to {
  transform: translateX(100%);
}
</style>
